/**
 * Access door creation logic
 * Should be called only after section length is finalized
 */
import { IAccessDoor } from '../../../models';
import {
  DEFAULT_COEFFICIENTS,
  IBotSection,
  SectionCreationContext,
} from '../../../models/bot';
import { dispatchMutation, dispatchQuery } from '../../../utils/dispatch.utils';
import { accessDoorApi, ahuApi } from '../../../services';

const DEFAULT_MOUNTING_PLANE_ID = 6;

export async function createAccessDoor(
  botSection: any,
  apiSection: any,
  accessory: any,
  context: SectionCreationContext,
  minWidth: number,
): Promise<void> {
  const { getOptions, onProgress } = context;
  const mountingPlaneId =
    accessory.mounting_plane_id ?? DEFAULT_MOUNTING_PLANE_ID;

  /** ------------------------------------------------------------------ */
  /** Fetch updated section (length values must be up to date) */
  /** ------------------------------------------------------------------ */
  const section =
    botSection.model ||
    ((await dispatchQuery(
      ahuApi.endpoints.getSection,
      apiSection.id,
      getOptions(),
    )) as any);

  /** ------------------------------------------------------------------ */
  /** Create access door */
  /** ------------------------------------------------------------------ */
  let accessDoor = (await dispatchMutation(
    accessDoorApi.endpoints.createAccessDoor,
    {
      body: {
        section_id: section.id,
        type: 'access_door',
      },
    },
    getOptions(),
  )) as IAccessDoor;

  /** ------------------------------------------------------------------ */
  /** Calculate minimal clearance */
  /** ------------------------------------------------------------------ */
  const minClearance = Math.max(
    accessDoor.clearances.min_clear_if_on_end_wall_from_sides,
    accessDoor.clearances.min_clear_if_on_side_wall_from_section_ups_downs,
  );

  const diff = accessDoor.width_calc - accessDoor.width;

  /** ------------------------------------------------------------------ */
  /** Adjust door width if section length is insufficient */
  /** ------------------------------------------------------------------ */
  const newWidth = calculateAccessDoorWidth(
    botSection,
    section.aw_length,
    minClearance,
    minWidth,
    diff
  );
  let changed = false;
  if (newWidth !== null && newWidth !== accessDoor.width) {
    changed = true;
    accessDoor = await dispatchMutation(
      accessDoorApi.endpoints.updateAccessDoor,
      {
        id: accessDoor.id,
        body: { width: newWidth },
      },
      getOptions(),
    );
  }

  /** ------------------------------------------------------------------ */
  /** Update door position */
  /** ------------------------------------------------------------------ */
  await updateAccessDoorPosition(
    botSection,
    section.aw_length,
    accessDoor,
    mountingPlaneId,
    minClearance,
    changed,
  );
  accessory.model = accessDoor;
  onProgress?.(DEFAULT_COEFFICIENTS.access_door);
}

/* ====================================================================== */
/* Helpers */
/* ====================================================================== */

function calculateAccessDoorWidth(
  botSection: IBotSection,
  sectionLength: number,
  minClearance: number,
  minWidth: number,
  diff: number
): number | null {
  const availableLength = getAvailableLength(
    botSection,
    sectionLength,
    minClearance,
    diff,
  );

  return Math.floor(Math.max(minWidth, Math.min(availableLength, 24)));
}

function getAvailableLength(
  botSection: IBotSection,
  sectionLength: number,
  minClearance: number,
  diff: number,
): number {
  if (botSection.type === 'coil') {
    const coilDepth = Math.max(botSection.coil_depth || 0, minClearance);
    return sectionLength - coilDepth - minClearance - diff;
  }

  return sectionLength - diff - minClearance * 2;
}

async function updateAccessDoorPosition(
  botSection: any,
  sectionLength: number,
  accessDoor: IAccessDoor,
  mountingPlaneId: number,
  minClearance: number,
  width_is_changed: boolean,
): Promise<void> {
  if (botSection.type === 'fan' || width_is_changed || mountingPlaneId !== DEFAULT_MOUNTING_PLANE_ID) {
    const desiredX = sectionLength - accessDoor.width_calc;
    const x = desiredX > 0 ? Math.max(desiredX, minClearance) : minClearance;

    await dispatchMutation(ahuApi.endpoints.updateAccessoryPosition, {
      id: accessDoor.id,
      body: { x, mounting_plane_id: mountingPlaneId },
    });

    return;
  }
}
